import type { ReactNode } from 'react'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * One titled block of a report — a summary table, a chart, a detail list.
 *
 * The legacy screen gave each of these a blue title bar with its own Export
 * Data button and its own pager, and that structure is worth keeping: a reader
 * scans the summary, checks the shape in the chart, then drops into the rows.
 * What is not worth keeping is the chrome. This is the same anatomy on a card.
 */
export function ReportSection({
  title,
  subtitle,
  onExport,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  /** Renders the Export Data button when given. */
  onExport?: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section className='overflow-hidden rounded-xl border bg-background shadow-sm'>
      <header className='flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5'>
        <div className='min-w-0'>
          <h4 className='truncate text-sm font-semibold text-foreground'>{title}</h4>
          {subtitle && <p className='truncate text-xs text-muted-foreground'>{subtitle}</p>}
        </div>
        {onExport && (
          <Button type='button' size='sm' variant='outline' className='h-8 gap-1.5' onClick={onExport}>
            <Download className='h-3.5 w-3.5' />
            Export data
          </Button>
        )}
      </header>
      {children}
      {footer && (
        <div className='flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-xs text-muted-foreground'>
          {footer}
        </div>
      )}
    </section>
  )
}

/** "Displaying 1–20 of 137", and the pager beside it. */
export function ReportPager({
  page,
  pageCount,
  from,
  to,
  total,
  onPage,
}: {
  page: number
  pageCount: number
  from: number
  to: number
  total: number
  onPage: (page: number) => void
}) {
  return (
    <>
      <span className='tabular-nums'>
        {total === 0 ? 'No rows' : `Displaying ${from}–${to} of ${total}`}
      </span>
      {pageCount > 1 && (
        <span className='flex items-center gap-1'>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-7 px-2 text-xs'
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            Previous
          </Button>
          <span className='px-1 tabular-nums'>
            Page {page} of {pageCount}
          </span>
          <Button
            type='button'
            size='sm'
            variant='outline'
            className='h-7 px-2 text-xs'
            disabled={page >= pageCount}
            onClick={() => onPage(page + 1)}
          >
            Next
          </Button>
        </span>
      )}
    </>
  )
}
